/**
 * TRAIN (EXPERT-ROOMS.md §15d, owner 2026-10-26) — the expert DEV room's twin of "Build context": one chip in the
 * composer's upper deck, "N new items · ~tokens · Train". Train reads what the room said since the last Train and
 * proposes training; "K to review" opens the suggestions (a popover — PortalMenu), each accepted or dismissed by a
 * person. Nothing lands without a person.
 */
import React, { useRef, useState } from 'react';
import { Loader2, Check, X } from 'lucide-react';
import PortalMenu from '@stepflow/ui-shared/components/PortalMenu.jsx';
import { formatTokens } from './ContextDiscussion.jsx';

const KIND = { answer: 'Answer', rule: 'Rule', procedure: 'Procedure' };

function SuggestionRow({ s, busy, onDecide }) {
  const label = s.kind === 'rule' && s.ruleId ? 'Rule change' : KIND[s.kind] || s.kind;
  return (
    <li className="px-3 py-2" data-testid={`train-suggestion-${s.id}`}>
      <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
        <span className="rounded bg-indigo-50 px-1.5 py-px font-medium text-indigo-700">{label}</span>
        {s.team ? <span>{s.team}</span> : null}
        {s.severity ? <span className="uppercase">{s.severity}</span> : null}
      </div>
      <div className="mt-1 text-xs text-gray-800">
        {s.kind === 'answer' ? (<><div className="font-medium">{s.question}</div><div className="text-gray-600">{s.answer}</div></>)
          : s.kind === 'procedure' ? (<><div className="font-medium">{s.title}</div>
            <div className="line-clamp-3 whitespace-pre-line text-gray-600">{s.text}</div></>)
            : (<>{s.before ? <div className="text-rose-700 line-through">{s.before}</div> : null}
              <div className={s.before ? 'text-emerald-700' : ''}>{s.statement}</div></>)}
      </div>
      {s.why ? <div className="mt-0.5 text-[11px] italic text-gray-400">“{s.why}”</div> : null}
      <div className="mt-1.5 flex gap-1">
        <button type="button" disabled={!!busy} onClick={() => onDecide('accept', s.id)} data-testid={`train-accept-${s.id}`}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
          {busy === `accept:${s.id}` ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <Check className="h-3 w-3" aria-hidden />}Accept
        </button>
        <button type="button" disabled={!!busy} onClick={() => onDecide('dismiss', s.id)} data-testid={`train-dismiss-${s.id}`}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors text-gray-600 hover:bg-gray-100 disabled:opacity-50">
          {busy === `dismiss:${s.id}` ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <X className="h-3 w-3" aria-hidden />}Dismiss
        </button>
      </div>
    </li>
  );
}

export default function ExpertTrainBar({ training = null, onTrain = null, onDecide = null }) {
  const anchor = useRef(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  if (!training) return null;
  const n = Number(training.messages || 0);
  const sugg = training.suggestions || [];
  const running = training.status === 'training';
  if (!n && !sugg.length && !running && !training.error) return null;
  const can = !!training.canTrain;
  const decide = async (act, id) => {
    setBusy(`${act}:${id}`);
    try { await onDecide?.(act, id); } finally { setBusy(''); }
  };
  return (
    <div data-testid="expert-train-status" className="ml-auto flex min-w-0 items-center justify-end gap-1.5 whitespace-nowrap text-[11px] text-gray-500">
      <span className="inline-flex items-stretch overflow-hidden rounded-md ring-1 ring-amber-200">
        {running ? <span className="inline-flex items-center gap-1 bg-indigo-50 px-2 py-0.5 text-indigo-700">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />Training…</span> : null}
        {!running && n > 0 ? <span className="bg-amber-50 px-2 py-0.5 text-amber-800" title={`${n} new item${n === 1 ? '' : 's'} not yet trained`}>
          {n} new item{n === 1 ? '' : 's'}</span> : null}
        {!running && n > 0 && training.tokens > 0 ? <span className="border-l border-gray-200 bg-white px-2 py-0.5 tabular-nums">
          ~{formatTokens(training.tokens)} tokens</span> : null}
        {!running && n > 0 && can ? (
          <button type="button" onClick={onTrain} data-testid="expert-train-button"
            title="Turn what the room settled into training for this expert — you review each suggestion"
            className="border-l border-amber-200 bg-indigo-600 px-2 py-0.5 text-[11px] font-medium text-white transition-colors hover:bg-indigo-700">
            Train
          </button>
        ) : null}
        {sugg.length ? (
          <button type="button" ref={anchor} onClick={() => setOpen((o) => !o)} data-testid="expert-train-review"
            className="border-l border-amber-200 bg-white px-2 py-0.5 font-medium text-indigo-700 hover:bg-indigo-50">
            {sugg.length} to review
          </button>
        ) : null}
      </span>
      {training.error && !running ? <span className="text-rose-600">{training.error}</span> : null}
      <PortalMenu anchorRef={anchor} open={open && sugg.length > 0} onClose={() => setOpen(false)} align="top-right"
        width={420} menuRole="dialog">
        <div className="max-h-[60vh] overflow-y-auto" data-testid="expert-train-panel">
          <div className="border-b border-gray-100 px-3 py-2 text-xs font-semibold text-gray-800">Training to review</div>
          <ul className="divide-y divide-gray-100">
            {sugg.map((s) => <SuggestionRow key={s.id} s={s} busy={can ? busy : 'locked'} onDecide={decide} />)}
          </ul>
        </div>
      </PortalMenu>
    </div>
  );
}
